<template>
    <DsModal
        :is-open="isOpen"
        size="xl"
        class="kmn-template-manager-modal"
        :show-close="false"
        @close="close"
    >
        <div class="template-manager">
            <section class="template-nav">
                <header class="template-nav-header">
                    <DsIconButton
                        name="x"
                        class="manager-close-button"
                        data-qa="close-manager"
                        @click="close"
                    />

                    <div class="header-text">
                        <h4 class="title">
                            Text message templates
                        </h4>

                        <p class="subtitle">
                            Sent from Keap Marketing Number
                        </p>
                    </div>

                    <DsIconButton
                        name="add-circle"
                        class="create-template-button"
                        data-qa="template-manager-create-template"
                        @click="createTemplate"
                    />
                </header>

                <div class="template-search">
                    <DsInputField
                        v-model="search"
                        type="search"
                        class="template-search-input"
                        input-class-list="template-search-input-field"
                        placeholder="Search text message templates"
                    >
                        <template #leading>
                            <DsIcon class="template-search-icon" name="search" />
                        </template>
                    </DsInputField>
                </div>

                <div v-if="showSearchResults" class="template-items filtered-items">
                    <KeapMarketingSmsTemplateItem
                        v-for="(template, i) in filteredTemplates"
                        :key="i"
                        :class="['template-item', { selected: isTemplateSelected(template)} ]"
                        @click.native="handleTemplateItemClick(template)"
                    >
                        <template #title>
                            <KeapMarketingSmsTemplateSearchHighlighter :highlight="search" :content="templateTitle(template)" />
                        </template>

                        <template #description>
                            <KeapMarketingSmsTemplateSearchHighlighter
                                :highlight="search"
                                :content="templateContent(template)"
                                :truncate-length="84"
                                truncate
                            />
                        </template>
                    </KeapMarketingSmsTemplateItem>
                </div>

                <div v-else-if="showEmptySearchResult" class="no-search-results">
                    <div class="no-templates-found">
                        No templates found containing
                    </div>

                    <div>
                        "{{ search }}"
                    </div>

                    <DsFilledButton
                        class="create-template-from-search"
                        data-qa="create-search-template"
                        dense
                        :loading="creatingTemplate"
                        @click="createTemplateFromSearch"
                    >
                        Add "{{ newTemplatePreview }}" now
                    </DsFilledButton>

                    <div v-if="showError" class="template-create-error">
                        There was a problem creating this template. Please try again.
                    </div>
                </div>

                <div v-else class="template-items">
                    <DsListItem
                        v-for="(template, i) in localTemplates"
                        :key="i"
                        class="template-item"
                        :class="{ selected: isTemplateSelected(template)}"
                        :title="templateTitle(template)"
                        :description="templateContent(template)"
                        @click.native="handleTemplateItemClick(template)"
                    />
                </div>

                <div class="share-feedback-container">
                    <DsTextButton
                        leading-icon="message-square"
                        data-qa="kmn-feedback-button"
                        @click="isFeedbackModalOpen = true"
                    >
                        Share feedback
                    </DsTextButton>
                </div>
            </section>

            <section v-if="selectedTemplate" :class="['template-editor', { open: editorOpen }]">
                <header class="template-editor-header">
                    <DsIconButton
                        name="arrow-left"
                        @click="setEditorOpen(false)"
                    />

                    <h4 class="edit-template">
                        Edit template
                    </h4>

                    <DsIconButton
                        v-if="showDelete"
                        :class="['delete-button-mobile', { 'disable-delete': saving }]"
                        name="trash-2"
                        @click="deleteTemplate"
                    />
                </header>

                <section :class="['template-editor-title', { 'hide-delete': !showDelete }]">
                    <span class="label">Template name: </span>

                    <DsInputField
                        ref="titleInput"
                        v-model="title"
                        placeholder="New template"
                        @input="handleEdit"
                    />

                    <DsIconButton
                        v-if="showDelete"
                        name="trash-2"
                        class="delete-button"
                        :disabled="saving"
                        @click="deleteTemplate"
                    />
                </section>

                <KeapMarketingNumberTemplateManagerTextBox
                    v-model="content"
                    :has-error="hasError"
                    :contains-supported-character="containsSupportedCharacter"
                    :max-length="maxLength"
                    :character-count-label="characterCountLabel"
                    :merge-field-categories="mergeFieldCategories"
                    :saving="saving"
                    :saved="saved"
                    @insert-template="insertTemplate"
                    @input="handleContentEdit"
                />
            </section>
            <SmsFeedbackModal v-if="isFeedbackModalOpen" source="Automations Templates" @close="isFeedbackModalOpen = false" />
        </div>
    </DsModal>
</template>

<script>
import axios from 'axios';
import debounce from 'lodash.debounce';

import SmsFeedbackModal from '@/components/shared/SmsFeedbackModal';
import KeapMarketingSmsTemplateSearchHighlighter from './KeapMarketingSmsTemplateSearchHighlighter';

import { TEMPLATE_KINDS, MEDIA_TYPES } from '../shared/template.constants.js';
import { containsSupportedCharacters } from './automatedSms.util';
import KeapMarketingNumberTemplateManagerTextBox from './KeapMarketingNumberTemplateManagerTextBox';
import { trackKmnTemplateInsert, trackKmnTemplateCreated, trackKmnTemplateDeleted } from '@/components/automatedSms/smsAnalytics';
import KeapMarketingSmsTemplateItem from './KeapMarketingSmsTemplateItem';
import { SMS_MESSAGE_LENGTH } from '@/components/broadcast/constants/smsBroadcast.constants';
import { getSmsSegments } from '@/components/broadcast/utils/sms-utils';

const BUTTON_PREVIEW_LENGTH = 24;

export default {
    components: {
        KeapMarketingNumberTemplateManagerTextBox,
        KeapMarketingSmsTemplateSearchHighlighter,
        SmsFeedbackModal,
        KeapMarketingSmsTemplateItem,
    },

    props: {
        editDelay: {
            type: Number,
            default: 500,
        },
        isOpen: Boolean,

        templates: Array,
        isSpaApp: Boolean,
        source: {
            type: String,
            default: 'automation',
        },
    },

    data() {
        return {
            selectedTemplate: null,
            editorOpen: false,
            saving: false,
            saved: false,
            creatingTemplate: false,
            showError: false,
            timeout: null,
            title: '',
            content: '',
            search: '',
            popoverOpen: false,
            isFeedbackModalOpen: false,
            localTemplates: [],
            mergeFieldCategories: [],
        };
    },

    created() {
        if (this.editDelay > 0) {
            this.handleEdit = debounce(this.handleEdit, this.editDelay);
        }
    },

    mounted() {
        this.localTemplates = this.templates;

        this.loadMergeFields();
        this.sort();
    },

    watch: {
        search: {
            handler() {
                this.showError = false;
            },
        },
        isOpen: {
            handler() {
                if (Array.isArray(this.templates) && this.templates.length > 0) {
                    this.setTemplate(this.templates[0]);
                }

                this.sort();
            },
        },

        templates: {
            handler(newVal, oldVal) {
                if (oldVal.length !== newVal.length) {
                    this.localTemplates = this.templates;
                    this.sort();
                }
            },
        },
    },

    computed: {
        maxLength() {
            return SMS_MESSAGE_LENGTH.MAXIMUM;
        },

        showDelete() {
            return Boolean(
                this.selectedTemplate
                    && this.selectedTemplate.id
                    && typeof this.selectedTemplate.id === 'string'
                    && !this.selectedTemplate.id.startsWith('default'),
            );
        },

        hasError() {
            return !this.containsSupportedCharacter || (this.characterCount > this.maxLength);
        },

        characterCount() {
            return this.content.length;
        },

        characterCountLabel() {
            return [
                this.$t('sms.broadcast.secondaryFooter.approx'),
                    `${this.characterCount}/${this.maxLength}`,
                    `(${this.$tc('sms.broadcast.secondaryFooter.messageCount', this.numSmsSegments, { count: this.numSmsSegments })})`,
            ].join(' ');
        },

        containsSupportedCharacter() {
            return containsSupportedCharacters(this.content);
        },

        filteredTemplates() {
            const { search, localTemplates } = this;

            const searchString = search.trim().toLowerCase();

            return localTemplates.filter(({ title = '', content = '' }) => {
                return title.toLowerCase().includes(searchString)
                    || content.toLowerCase().includes(searchString);
            });
        },

        newTemplatePreview() {
            const { search } = this;

            if (search.length > BUTTON_PREVIEW_LENGTH) {
                return `${search.slice(0, BUTTON_PREVIEW_LENGTH)}...`;
            }

            return search;
        },

        isSearching() {
            return Boolean(this.search);
        },

        noSearchResults() {
            return this.filteredTemplates.length === 0;
        },

        showSearchResults() {
            return this.isSearching && !this.noSearchResults;
        },

        showEmptySearchResult() {
            return this.isSearching && this.noSearchResults;
        },

        numSmsSegments() {
            const { numMessages } = getSmsSegments(this.content);

            return numMessages ?? 0;
        },
    },

    methods: {
        sort() {
            this.localTemplates.sort((a, b) => {
                const textA = a.title?.toUpperCase() || 'New template';
                const textB = b.title?.toUpperCase() || 'New template';

                return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
            });
        },

        async loadMergeFields() {
            try {
                const params = new URLSearchParams({ usage: 'sms' });

                ['contact', 'contactCustomFields', 'profile', 'today', 'user'].forEach((category) => {
                    params.append('categories', category);
                });

                const { data } = await axios.get(`/app/merge/fields?${params.toString()}`);

                this.mergeFieldCategories = data.categories;
            } catch (e) {
                this.$toast({ message: 'There was an error loading the available merge fields. Please try again.' });
            }
        },

        async createTemplateFromSearch() {
            const { search } = this;

            this.creatingTemplate = true;

            const template = await this.createTemplate({ title: search });

            if (template) {
                this.moveTemplateUp(template);
            }

            this.search = '';

            this.creatingTemplate = false;
        },

        async createTemplate({ title = '', content = '' }) {
            try {
                const { data } = await axios.post('/app/templateFunnel/templates', {
                    mediaType: MEDIA_TYPES.PLAIN_TEXT,
                    templateKind: this.source === 'broadcast' ? TEMPLATE_KINDS.KMN_BROADCAST_CUSTOM : TEMPLATE_KINDS.KMN_AUTOMATION_CUSTOM,
                    title,
                    subject: '',
                    content,
                });

                const createdTemplate = data;

                trackKmnTemplateCreated(this.isSpaApp, this.source === 'automation' ? 'Automations' : 'Broadcasts');

                this.setEditorOpen(true);
                this.setTemplate(createdTemplate);
                this.localTemplates.unshift(createdTemplate);

                return createdTemplate;
            } catch (error) {
                this.showError = true;

                return null;
            }
        },

        async deleteTemplate() {
            this.saving = false;
            const templateId = this.selectedTemplate.id;

            try {
                await this.$confirm({
                    title: 'Delete template?',
                    message: "Once you delete this template, it's gone forever.",
                    confirmLabel: 'Yes, delete',
                    destructive: true,
                    cancelLabel: 'No, cancel',
                });
            } catch {
                return;
            }

            await axios.delete(`/app/templateFunnel/templates/${templateId}`);

            trackKmnTemplateDeleted(this.selectedTemplate, this.isSpaApp, this.source === 'automation' ? 'AA management modal' : 'Broadcasts management modal');

            this.$emit('load-templates');
            this.setEditorOpen(false);

            this.localTemplates = this.localTemplates.filter((template) => template.id !== templateId);
            this.setTemplate(this.localTemplates[0]);
        },

        handleTemplateItemClick(template) {
            this.setEditorOpen(true);
            this.setTemplate(template);
        },

        handleContentEdit() {
            if (this.content === this.selectedTemplate.content) {
                return;
            }

            this.handleEdit();
        },

        async handleEdit() {
            this.saving = true;
            this.saved = false;
            const templateKind = this.selectedTemplate.templateKind || (this.source === 'broadcast' ? TEMPLATE_KINDS.KMN_BROADCAST_CUSTOM : TEMPLATE_KINDS.KMN_AUTOMATION_CUSTOM);
            const { content, title } = this;
            const templateId = this.selectedTemplate.id;

            if (templateId.startsWith('default')) {
                const { data } = await axios.post('/app/templateFunnel/templates', {
                    mediaType: MEDIA_TYPES.PLAIN_TEXT,
                    templateKind,
                    title,
                    subject: '',
                    content,
                });

                this.selectedTemplate = data;

                this.moveTemplateUp(data);
            } else {
                const { data } = await axios.put(`/app/templateFunnel/templates/${templateId}`, {
                    mediaType: MEDIA_TYPES.PLAIN_TEXT,
                    templateKind,
                    title,
                    subject: '',
                    content,
                });

                this.selectedTemplate = data;

                this.moveTemplateUp(data);
            }

            this.saving = false;
            this.saved = true;
        },

        moveTemplateUp(movingTemplate) {
            if (!movingTemplate) {
                return;
            }

            const templateIndex = this.localTemplates.findIndex(((template) => template.id === movingTemplate.id));

            if (templateIndex > -1) {
                this.localTemplates.splice(templateIndex, 1);
                this.localTemplates.unshift(movingTemplate);
            }
        },

        insertTemplate() {
            const { id, title, templateKind } = this.selectedTemplate;
            const template = {
                id,
                content: this.content,
                title,
                templateKind,
            };

            this.$emit('insert-template', template);

            trackKmnTemplateInsert(template, this.source === 'automation' ? 'AA management modal' : 'Broadcasts management modal', this.isSpaApp);

            this.close();
        },

        isTemplateSelected(template) {
            return this.selectedTemplate
                ? this.selectedTemplate.id === template.id
                : false;
        },

        close() {
            this.$emit('close');
        },

        setEditorOpen(value) {
            this.editorOpen = value;
        },

        setTemplate(template = {}) {
            this.selectedTemplate = template;
            this.title = template.title || '';
            this.content = template.content || '';
            this.saved = false;
        },

        templateContent(template) {
            return template.content || '(No content)';
        },

        templateTitle(template) {
            return template.title || 'New template';
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
    $template-manager-height: #{px-to-rem(700px)};
    $template-nav-width: #{px-to-rem(360px)};

    .kmn-template-manager-modal {
        --modal-padding: 0;
    }

    .template-manager {
        display: flex;
        height: $template-manager-height;

        @media($small) {
            height: 100vh;
            flex-direction: column;
            justify-content: space-between;
        }
    }

    .template-nav {
        display: flex;
        flex-direction: column;
        width: 100%;
        max-width: $template-nav-width;
        z-index: 1;

        @media($small) {
            flex: 1;
            min-height: 0;
            max-width: 100%;
        }
    }

    .title {
        font-weight: $font-weight-bold;
        font-size: $font-size-lg;
    }

    .subtitle {
        margin: $spacing-050 0 0;
    }

    .hide-template-nav,
    .insert-template{
        @media($small) {
            display: none;
        }
    }

    .template-nav-header {
        display: flex;
        padding: $spacing-300 $spacing-200 $spacing-200;

        @media($small) {
            width: 100%;
        }
    }

    .template-search {
        padding: $spacing-200 $spacing-200 0;

        .input-field {
            height: 2rem;
            padding-right: $spacing-700;
            margin-bottom: $spacing-200;
        }

        input {
            width: auto;
        }
    }

    .template-search-input ::placeholder {
        color: $color-text-subtle;
    }

    .template-search-input input:focus + div .icon {
        color: $color-blue;
    }

    .template-create-error {
        padding-top: $spacing-200;
        color: $color-red;
    }

    .no-search-results {
        padding: $spacing-100 $spacing-300;
        color: $color-text-subtle;

        .create-template-from-search {
            margin-top: $spacing-100;
        }
    }

    .template-items {
        @include overflowY;
    }

    .template-item {
        cursor: pointer;

        .list-item-info {
            padding: $spacing-200;
        }

        &.selected {
            background-color: $color-gray-100;
        }

        &:hover {
            background-color: $color-gray-050;
        }

        @media ($small) {
            &.selected {
                background-color: $color-paper;
            }
        }
    }

    .template-editor {
        display: flex;
        flex-direction: column;
        background-color: $color-paper;
        width: 100%;
        @include border-start(1px solid $color-gray-200);
        @include border-radius(0 $border-radius $border-radius 0);


        @media($small) {
            @include transition(transform);

            transform: translateX(100%);
            @include position(absolute 0 0 auto auto);
            z-index: 5;
            height: 100vh;

            &.open {
                transform: translateX(0);
            }

            &:not(.open) {
                transform: translateX(100%);
            }
        }
    }

    .template-editor-header,
    .insert-template-mobile {
        display: none;

        @media($small) {
            display: flex;
        }
    }

    .edit-template {
        flex: 1;
        margin: 0 $spacing-100;
    }

    .template-nav-header,
    .template-editor-header,
    .template-editor-title {
        border-bottom: 1px solid $color-gray-200;
    }

    .template-editor-header,
    .template-button-row {
        padding: $spacing-200;
    }

    .template-editor-title {
        display: flex;
        align-items: center;
        font-size: $font-size-md;
        padding: $spacing-100;
        white-space: nowrap;

        --input-margin-bottom: 0;
        --input-padding: 0;
        --input-border: none;
        --input-focus-border: none;
        --input-height: #{$spacing-300};

        .label {
            padding: 0 $spacing-100;
            color: $color-gray-700;
        }

        &.hide-delete {
            padding: $spacing-200 $spacing-100;
        }
    }

    .delete-button {
        &-mobile {
            display: none;

            &.disable-delete {
                --icon-color: #{$color-gray-700};
            }
        }

        &:disabled {
            background-color: $color-paper;
        }

        @media($small) {
            display: none;

            &-mobile {
                display: block;
            }
        }
    }

    .message-content {
        border: none;
        font-size: $font-size-md;
        margin-bottom: $spacing-100;
        resize: none;
        padding: $spacing-200;
        flex: 1;
        outline: none;
    }

    .template-button-row {
        display: flex;
        align-items: center;
        border-top: 1px solid $color-gray-200;
    }

    .action-buttons {
        display: flex;
        flex: 1;
    }

    .saving {
        display: flex;
        align-items: center;
        color: $color-gray-700;
        --icon-size: 1rem;
        font-size: $font-size-xs;
        padding: 0 $spacing-200;

        .icon {
            animation: spin 4s infinite;
            padding: $spacing-025;
        }
    }

    .saved {
        padding: 0 $spacing-200;
    }

    .manager-close-button {
        margin: 0 0.8rem 0 0;
        padding: 0;
        height: fit-content;
        width: fit-content;
    }

    .create-template-button {
        margin: 0 0 0 $spacing-300;
        padding: 0;
        height: fit-content;
        width: fit-content;
    }

    .character-count {
        color: $color-text-subtle;
        align-self: end;
        margin-top: auto;
        margin-left: auto;
        font-size: px-to-rem(12px);
    }

    .footer {
        padding: 0 $spacing-200 $spacing-200;
        display: flex;
        flex-direction: row;
    }

    .character-count-error {
        color: $color-red;
        display: inline-block;
        max-width: 60%;
        font-size: px-to-rem(14px);
    }

    .share-feedback-container {
        margin-top: auto;
        justify-content: center;
        display: flex;
        padding: $spacing-050;
    }
</style>

<style lang="scss">
.kmn-template-manager-modal {
    .template-item .description {
        -webkit-line-clamp: 2;
    }

}
</style>
